import { BRAND } from "../brand";
import { lifecycle } from "./lifecycle";

import { documents } from "./documents";
import { comms, notificationsEn } from "./comms";
import { data } from "./data";
import { platform } from "./platform";

/**
 * İngilizce katalog. Eksik anahtarlar Türkçeye düşer; `pnpm --filter @hik/i18n test`
 * eksik anahtarları raporlar.
 */
import { expenses } from "./expenses";

export const en = {
  expenses,
  lifecycle,
  comms,
  notifications: notificationsEn,
  brand: { name: BRAND.name, tagline: BRAND.tagline.en },
  documents,
  units: { days_one: "{count} day", days_other: "{count} days", hours_one: "{count} hour", hours_other: "{count} hours" },
  errors: {
    validation_failed: "Please check the highlighted fields.",
    unauthenticated: "Please sign in to continue.",
    session_expired: "Your session has expired. Please sign in again.",
    forbidden: "You don't have permission for this. Contact your HR team if you need access.",
    not_found: "The record was not found or is not available to you.",
    conflict: "This record conflicts with another change.",
    stale_version: "This record changed while you were viewing it. Reload and try again.",
    idempotency_conflict: "This was just done from another window or device with different details. Refresh to see the current state.",
    rate_limited: "Too many attempts. Please try again in a few minutes.",
    invalid_credentials: "Email or password is incorrect.",
    account_locked: "Too many failed attempts; the account is temporarily locked. Try again in 15 minutes or reset your password.",
    email_taken: "An account with this email already exists. Try signing in.",
    invitation_invalid: "This invitation link is invalid or already used. Ask your HR team for a new one.",
    invitation_expired: "This invitation has expired. Ask your HR team for a new one.",
    token_invalid: "This link is invalid or has expired. Request a new one.",
    weak_password: "Password must be at least 10 characters.",
    insufficient_balance: "Not enough balance: {available} available, {requested} requested.",
    overlapping_request: "You already have a leave request on these dates.",
    no_working_days: "There are no working days in the selected dates, so nothing would be deducted.",
    invalid_date_range: "End date cannot be before the start date.",
    notice_period: "This leave type must be requested at least {days} days in advance.",
    request_not_editable: "This request can no longer be changed.",
    not_your_turn: "This request is not waiting for your approval.",
    self_approval: "You cannot approve your own request.",
    file_too_large: "Files can be at most {maxMb} MB.",
    file_type_not_allowed: "Only PDF, JPG, PNG, HEIC or WEBP files are allowed.",
    import_has_errors: "Some rows in the file need fixing.",
    already_done: "This has already been done.",
    precondition_failed: "Complete the required steps first.",
    internal_error: "Something went wrong. Nothing was saved; please try again.",
    network: "Couldn't connect. Check your connection and try again; your input is kept.",
    reasons: {
      support_read_only: "Changes can't be made in a support session. Someone at the company has to do this.",
      support_no_files: "File contents, payslips and exports can't be opened in a support session.",
      support_ended: `The ${BRAND.name} support session has ended.`,
      support_access_disabled: `This company has turned off ${BRAND.name} support access. Ask the company to turn it on.`,
      support_active: "You already have an open support session. End it before starting a new one.",
      tenant_suspended: `Your company's ${BRAND.name} account is suspended. Contact your company administrator.`,
      no_active_membership: "Your account has been closed or suspended. Contact your company's HR team to reopen it.",
      last_admin: "This person is the company's last system administrator. Give the system administrator role to someone else first.",
      admin_account: "Only system administrators can suspend or reopen a system administrator's account.",
      separation_of_duties: "You approved an earlier step of this record; someone else in company management gives the approval in place of finance.",
      ip_not_allowed: "The admin panel can't be reached from this network.",
      job_not_failed: "Only failed jobs can be retried.",
      platform_locked: "Too many failed attempts; the panel account is locked for 30 minutes. Try again later.",
      totp_required: "Enter the 6-digit code from your authenticator app.",
      totp_invalid: "The verification code is wrong or has already been used. Enter the current code from the app.",
      totp_not_enrolled:
        "Two-step verification isn't turned on for this panel account and is required to sign in. Turn it on from the command line: platform-admin totp-enable --email <email>",
    },
  },
  fieldErrors: {
    required: "This field is required.",
    invalid: "Enter a valid value.",
    too_small: "Too short.",
    too_big: "Too long.",
    invalid_email: "Enter a valid email address.",
    national_id_format: "Turkish ID number must be 11 digits and not start with 0.",
    national_id_checksum: "Turkish ID number could not be verified; check the digits.",
    iban_format: "IBAN must start with \"TR\" and be 26 characters.",
    invalid_date_range: "End date cannot be before the start date.",
    invalid_amount: "Enter a valid amount.",
    duplicate: "This value is already used by another record.",
    weak_password: "Password must be at least 10 characters.",
    wrong_password: "Incorrect password.",
    code_too_short: "The code is 8 characters; part of it may be missing.",
  },
  leave: {
    explain: {
      working_days: "{count} working days will be deducted.",
      working_days_half: "{count} working days will be deducted (including half days).",
      hours: "{count} hours will be deducted.",
      weekend_skipped_one: "{count} weekend day not counted.",
      weekend_skipped_other: "{count} weekend days not counted.",
      short_day_one: "A half working day (per your work schedule) counted as 0.5 day.",
      short_day_other: "{count} half working days (per your work schedule) counted as 0.5 day each.",
      holiday_skipped: "{name} ({date}) is a public holiday and is not counted.",
      half_holiday: "{name} ({date}) is a half-day holiday; only the morning is counted.",
      calendar_days: "This leave type counts calendar days: {count} days.",
      no_balance_tracking: "This leave type does not use a balance.",
      return_date: "Back at work: {date}.",
    },
    approver: { manager: "Manager", hr: "HR", hr_pool: "HR team" },
    waiting: { person: "Waiting for {name}", pool: "Waiting for {label}" },
  },
  ui: {
    common: {
      save: "Save", cancel: "Cancel", close: "Close", back: "Back", continue: "Continue", loading: "Loading…",
      retry: "Try again", search: "Search", edit: "Edit", delete: "Delete", undo: "Undo", yes: "Yes", no: "No",
      optional: "optional", all: "All", none: "None", today: "Today", download: "Download", export: "Export",
    },
  },
  data,
  platform,
};
